/**
 * Reference pickers shared by the inventory screens.
 *
 * Inventory commands are addressed by UUID (`variant_id`, `warehouse_id`,
 * `uom_id`, `location_id`, `batch_id`), so every posting form needs a resolved
 * picker. The catalog-owned lookups (variants, UoMs, categories) and the
 * platform branch list are permission-gated by the caller: a user without the
 * read grant sees an explainer instead of a control that would 403.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import {
  useBatchOptions,
  useBranchOptions,
  useCategoryOptions,
  useLocationOptions,
  useUomOptions,
  useVariantSearch,
  useWarehouseOptions,
} from "../queries";

/** Sentinel for "no filter"; Radix Select forbids an empty-string value. */
export const ALL = "__all__";

export interface SelectPickerProps {
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  /** Adds an "All" choice that resolves to null. */
  includeAll?: boolean;
  allLabel?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Radix keeps a Select controlled for its whole lifetime, so an unselected
 * picker resolves to "" (or the "all" sentinel) — never `undefined`.
 */
function toSelectValue(value: string | null, includeAll: boolean): string {
  if (value !== null && value !== "") return value;
  return includeAll ? ALL : "";
}

export function WarehouseSelect({
  id,
  value,
  onChange,
  disabled,
  includeAll = false,
  allLabel,
  placeholder,
  className,
}: SelectPickerProps) {
  const { t } = useTranslation();
  const warehouses = useWarehouseOptions(!(disabled ?? false));
  return (
    <Select
      value={toSelectValue(value, includeAll)}
      disabled={disabled}
      onValueChange={(next) => onChange(next === ALL ? null : next)}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue
          placeholder={placeholder ?? t("inventory.common.pickWarehouse", "Warehouse…")}
        />
      </SelectTrigger>
      <SelectContent>
        {includeAll ? (
          <SelectItem value={ALL}>{allLabel ?? t("inventory.common.all", "All")}</SelectItem>
        ) : null}
        {(warehouses.data ?? []).map((warehouse) => (
          <SelectItem key={warehouse.id} value={warehouse.id}>
            {warehouse.code} — {warehouse.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface LocationSelectProps extends SelectPickerProps {
  warehouseId: string | null;
  /** Hidden entirely when the warehouse does not track bins. */
  enabled?: boolean;
}

export function LocationSelect({
  id,
  warehouseId,
  value,
  onChange,
  disabled,
  includeAll = false,
  allLabel,
  placeholder,
  enabled = true,
  className,
}: LocationSelectProps) {
  const { t } = useTranslation();
  const locations = useLocationOptions(warehouseId, enabled && !(disabled ?? false));
  return (
    <Select
      value={toSelectValue(value, includeAll)}
      disabled={disabled ?? warehouseId === null}
      onValueChange={(next) => onChange(next === ALL ? null : next)}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue
          placeholder={placeholder ?? t("inventory.common.pickLocation", "Default location")}
        />
      </SelectTrigger>
      <SelectContent>
        {includeAll ? (
          <SelectItem value={ALL}>{allLabel ?? t("inventory.common.all", "All")}</SelectItem>
        ) : null}
        {(locations.data ?? []).map((location) => (
          <SelectItem key={location.id} value={location.id}>
            {location.code} —{" "}
            {t(`inventory.locationType.${location.location_type}`, location.location_type)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function UomSelect({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  canRead = true,
  className,
}: SelectPickerProps & { canRead?: boolean }) {
  const { t } = useTranslation();
  const uoms = useUomOptions(canRead && !(disabled ?? false));
  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground" role="note">
        {t("inventory.common.uomPermission", "Unit-of-measure lookup needs catalog.uom.read.")}
      </p>
    );
  }
  return (
    <Select value={value ?? ""} disabled={disabled} onValueChange={(next) => onChange(next)}>
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={placeholder ?? t("inventory.common.pickUom", "Unit…")} />
      </SelectTrigger>
      <SelectContent>
        {(uoms.data ?? []).map((uom) => (
          <SelectItem key={uom.id} value={uom.id}>
            {uom.code} — {uom.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function CategorySelect({
  id,
  value,
  onChange,
  disabled,
  includeAll = true,
  allLabel,
  placeholder,
  canRead = true,
  className,
}: SelectPickerProps & { canRead?: boolean }) {
  const { t } = useTranslation();
  const categories = useCategoryOptions(canRead && !(disabled ?? false));
  if (!canRead) return null;
  return (
    <Select
      value={toSelectValue(value, includeAll)}
      disabled={disabled}
      onValueChange={(next) => onChange(next === ALL ? null : next)}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={placeholder ?? t("inventory.common.pickCategory", "Category…")} />
      </SelectTrigger>
      <SelectContent>
        {includeAll ? (
          <SelectItem value={ALL}>{allLabel ?? t("inventory.common.all", "All")}</SelectItem>
        ) : null}
        {(categories.data ?? []).map((category) => (
          <SelectItem key={category.id} value={category.id}>
            {category.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function BranchSelect({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  canRead = true,
  className,
}: SelectPickerProps & { canRead?: boolean }) {
  const { t } = useTranslation();
  const branches = useBranchOptions(canRead && !(disabled ?? false));
  if (!canRead) return null;
  return (
    <Select value={value ?? ""} disabled={disabled} onValueChange={(next) => onChange(next)}>
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={placeholder ?? t("inventory.common.pickBranch", "Branch…")} />
      </SelectTrigger>
      <SelectContent>
        {(branches.data ?? []).map((branch) => (
          <SelectItem key={branch.id} value={branch.id}>
            {branch.code} — {branch.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface BatchSelectProps extends SelectPickerProps {
  variantId: string | null;
  warehouseId: string | null;
  canRead?: boolean;
}

/** Batch / serial identity, only offered for variants that carry batches. */
export function BatchSelect({
  id,
  variantId,
  warehouseId,
  value,
  onChange,
  disabled,
  canRead = true,
  className,
}: BatchSelectProps) {
  const { t } = useTranslation();
  const batches = useBatchOptions(
    variantId ?? undefined,
    warehouseId ?? undefined,
    canRead && !(disabled ?? false),
  );
  const options = batches.data ?? [];
  if (!canRead || options.length === 0) return null;
  return (
    <Select
      value={toSelectValue(value, true)}
      disabled={disabled}
      onValueChange={(next) => onChange(next === ALL ? null : next)}
    >
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={t("inventory.common.pickBatch", "Batch…")} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{t("inventory.common.noBatch", "No batch")}</SelectItem>
        {options.map((batch) => (
          <SelectItem key={batch.id} value={batch.id}>
            {batch.batch_no}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface VariantPickerProps {
  id: string;
  value: string | null;
  onChange: (variantId: string | null, sku: string | null) => void;
  disabled?: boolean;
  canRead?: boolean;
  /** SKU of the current selection, so the trigger stays labelled across searches. */
  selectedSku?: string | null;
  className?: string;
}

/**
 * SKU picker. The server filter `q` is the catalog's citext picker search, so
 * an operator can reach any variant; the combobox itself filters the loaded
 * page client-side.
 */
export function VariantPicker({
  id,
  value,
  onChange,
  disabled,
  canRead = true,
  selectedSku,
  className,
}: VariantPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const variants = useVariantSearch(search, canRead && !(disabled ?? false));

  const options = useMemo(() => {
    const loaded = (variants.data ?? []).map((variant) => ({
      value: variant.id,
      label: variant.sku,
    }));
    if (
      value !== null &&
      selectedSku !== null &&
      selectedSku !== undefined &&
      !loaded.some((option) => option.value === value)
    ) {
      return [{ value, label: selectedSku }, ...loaded];
    }
    return loaded;
  }, [variants.data, value, selectedSku]);

  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground" role="note">
        {t("inventory.common.variantPermission", "SKU lookup needs catalog.variant.read.")}
      </p>
    );
  }

  return (
    <div className={className}>
      <Combobox
        id={id}
        options={options}
        value={value}
        onChange={(next) =>
          onChange(
            next,
            next === null ? null : (options.find((o) => o.value === next)?.label ?? null),
          )
        }
        disabled={disabled}
        placeholder={t("inventory.common.pickVariant", "Choose a SKU…")}
        searchPlaceholder={t("inventory.common.filterLoaded", "Filter loaded SKUs…")}
        emptyMessage={t("inventory.common.noVariants", "No SKUs match.")}
      />
      <div className="mt-1.5 flex flex-col gap-1">
        <Label htmlFor={`${id}-search`} className="text-xs text-muted-foreground">
          {t("inventory.common.variantServerSearch", "Search all SKUs")}
        </Label>
        <Input
          id={`${id}-search`}
          // SKUs are Latin codes — LTR even inside the RTL layout.
          dir="ltr"
          value={search}
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("inventory.common.variantSearchPlaceholder", "Type a SKU…")}
        />
      </div>
    </div>
  );
}
